/**
 * A component to render SVG lines for the directory tree.
 *
 * ディレクトリツリーの階層線を描画する SVG コンポーネント。
 */

import type { TreeLineProps } from "./types"

/**
 * Renders SVG lines for a directory tree structure.
 *
 * ディレクトリツリー構造の SVG ラインを描画。
 *
 * @param props - コンポーネントの Props。
 * @returns TreeLine コンポーネント。
 */
const TreeLine = ({ items, itemHeight, width, segmentWidth, color = "#a0aec0", strokeWidth = 1, renderFromY = 0, renderHeight, renderStartIndex, renderEndIndex, lookaheadEndIndex }: TreeLineProps) => {
    const paths: string[] = []

    const effectiveRenderHeight = renderHeight ?? items.length * itemHeight
    const computedStartIndex = (() => {
        if (typeof renderStartIndex === "number" && Number.isFinite(renderStartIndex)) {
            return Math.max(0, Math.floor(renderStartIndex))
        }
        return Math.max(0, Math.floor(renderFromY / itemHeight))
    })()
    const defaultEndExclusive = Math.min(items.length, Math.ceil((renderFromY + effectiveRenderHeight) / itemHeight))
    const computedEndExclusive = (() => {
        if (typeof renderEndIndex === "number" && Number.isFinite(renderEndIndex)) {
            const endInclusive = Math.floor(renderEndIndex)
            return Math.max(Math.min(items.length, endInclusive + 1), computedStartIndex)
        }
        return Math.max(defaultEndExclusive, computedStartIndex)
    })()
    const lookaheadExclusive = (() => {
        if (typeof lookaheadEndIndex === "number" && Number.isFinite(lookaheadEndIndex)) {
            const endInclusive = Math.floor(lookaheadEndIndex)
            const candidate = Math.min(items.length, endInclusive + 1)
            return Math.max(candidate, computedEndExclusive)
        }
        return Math.max(defaultEndExclusive, computedEndExclusive)
    })()

    for (let itemIndex = computedStartIndex; itemIndex < computedEndExclusive; itemIndex++) {
        const item = items[itemIndex]
        const yBase = itemIndex * itemHeight
        const yCenter = yBase + itemHeight / 2

        // 祖先の線を描画
        for (let i = 0; i < item.indentLevel; i++) {
            const x = i * segmentWidth + segmentWidth / 2
            // この祖先ポイントから下に線を引くべきかどうかを確認
            // これは、同じまたはより深いレベルの後続アイテムが
            // この祖先の子であるかどうかに依存
            let drawVerticalLine = false
            for (let k = itemIndex; k < lookaheadExclusive; k++) {
                if (items[k].indentLevel > i && items[k].ancestorIsLast.length > i && !items[k].ancestorIsLast[i]) {
                    drawVerticalLine = true
                    break
                }
                if (items[k].indentLevel === i && k > itemIndex) {
                    // 関連する兄弟要素を過ぎた
                    break
                }
            }

            // 祖先の縦線にはアイコンスペースを設けない
            // drawVerticalLine は、その祖先ラインが下まで続く必要があるかどうかを判定
            // !item.ancestorIsLast[i] は、現在のアイテムの祖先 (i) が最後の子ではないことを確認 (つまり、線が続くべき)
            if (drawVerticalLine && item.ancestorIsLast.length > i && !item.ancestorIsLast[i]) {
                paths.push(`M ${x},${yBase} L ${x},${yBase + itemHeight}`)
            }
        }

        // 現在のアイテムの接続線を描画
        const connectorXBase = item.indentLevel * segmentWidth
        const connectorX = connectorXBase + segmentWidth / 2

        // L 字または T 字の垂直部分
        if (item.isDirectory) {
            const iconSpace = segmentWidth / 1.5 // アイコンスペースを調整
            paths.push(`M ${connectorX},${yBase} L ${connectorX},${yCenter - iconSpace}`) // アイコンスペースの上端までの線、上側のスペースを広げる
        } else {
            paths.push(`M ${connectorX},${yBase} L ${connectorX},${yCenter}`) // ファイルの場合は完全な線
        }

        // L 字または T 字の水平部分
        // ディレクトリ、または最後の子であるファイルの場合に描画
        if (item.isDirectory) {
            // ディレクトリの場合は、展開アイコンのために線に間を空ける
            // 右線は不要なので、左側の短い線のみ描画、もしくは何も描画しない
            // 現状では右線だけでなく、水平線全体が不要という解釈で何もしない
        } else if (item.isLastChild) {
            // ディレクトリではなく、最後の子であるファイルの場合
            paths.push(`M ${connectorX},${yCenter} L ${connectorXBase + segmentWidth},${yCenter}`)
        }

        // 兄弟の中で最後の子でない場合、垂直線を下に伸ばす
        if (!item.isLastChild) {
            if (item.isDirectory) {
                const iconSpace = segmentWidth / 1.5 // アイコンスペースを調整
                paths.push(`M ${connectorX},${yCenter + iconSpace} L ${connectorX},${yBase + itemHeight}`) // 下側のスペースを広げる
            } else {
                paths.push(`M ${connectorX},${yCenter} L ${connectorX},${yBase + itemHeight}`)
            }
        }
    }

    const svgHeight = items.length * itemHeight

    return (
        <svg
            width={width}
            height={renderHeight ?? svgHeight}
            viewBox={`0 ${renderFromY} ${width} ${renderHeight ?? svgHeight}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="pointer-events-none absolute top-0 left-0 z-0 opacity-75" // コンテンツの背面に配置し、透明度を追加
        >
            <title>Tree Lines</title>
            {paths.map((d, index) => (
                <path key={`path-${items[computedStartIndex + index]?.id || index}-${computedStartIndex + index}`} d={d} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            ))}
        </svg>
    )
}

export { TreeLine }
